import React, { useState, useEffect } from "react";

import Flex from "../../components/Flex/Flex";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { capitalCase } from "capital-case";
// import SmallPrint from "../../components/SmallPrint/SmallPrint";
import { getDataPoints } from "../compareSweden";
import { CompareSwedenData } from "./models/CompareSwedenData";
import BarChart from "../../components/BarChart/BarChart";
import "./CompareSweden.css";
import TagFilter from "../../components/TagFilter/TagFilter";
import Loading from "../../components/Loading/Loading";

type DataState = {
    datapoints: CompareSwedenData[];
    countries: string[];
    categories: string[];
};

type FilterState = {
    countries: string[] | null;
    category: string | null;
};

type ResultState = {
    datapoints: number[];
    unit: string;
};

function CompareSweden() {
    let location = useLocation();
    let locationHash = queryString.parse(location.search);

    const [data, setData] = useState<DataState>({
        datapoints: [],
        countries: [],
        categories: []
    });

    const [filter, setFilter] = useState<FilterState>({
        countries: null,
        category: null
    });

    const [result, setResult] = useState<ResultState>({ datapoints: [], unit: "" });

    const [image, setImage] = useState<{ url: string; name: string }>();

    useEffect(() => {
        getDataPoints().then(datapoints => {
            const countries = Object.keys(datapoints[0]).filter(key => key !== "unit" && key !== "topic"),
                categories = datapoints.map(point => point.topic);

            setData({
                datapoints,
                countries,
                categories
            });

            // default filter
            setFilter({
                countries: countries,
                category: categories[0]
            });
        });
    }, []);

    useEffect(() => {
        const point = data.datapoints.find(point => point.topic === filter.category);

        if (point == null) {
            return;
        }

        let values = filter.countries
            ?.map(country => point[country as keyof CompareSwedenData])
            .map(val => parseFloat(val.replace(/[\s%]/g, "").replace(",", ".")));

        setResult({
            datapoints: values ?? [],
            unit: point.unit
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    if (data.datapoints.length === 0) {
        return <Loading />;
    }
    const download = locationHash.download === "1";

    return (
        <>
            <Flex cols={1}>
                <TagFilter
                    title="Data category (select max one)"
                    onChange={item => setFilter({ ...filter, category: item })}
                    value={filter.category}
                    options={data.categories}
                    extractTitle={item => item}
                    extractValue={item => item}
                    multiple={false}
                />

                <TagFilter
                    title="Market"
                    onChange={items => setFilter({ ...filter, countries: items })}
                    value={filter.countries}
                    options={data.countries}
                    extractTitle={item => item}
                    extractValue={item => item}
                    multiple
                />
            </Flex>

            <BarChart
                title={filter.category ?? ""}
                data={result.datapoints}
                labels={filter.countries?.map(country => capitalCase(country)) ?? []}
                xLabel={`${filter.category} (${result.unit})`}
                yLabel="Country"
                onImageUpdate={setImage}
            />

            <Flex className="CompareSweden-bottom" cols={download ? 2 : 1}>
                {/* 
                - Temporarily disabled until it can be rendered automatically

                <SmallPrint>
                    <p>
                        Sustainable Development Goal Index: The SDG Index score signifies a country’s position between
                        the worst (0) and best (100) outcomes. Sweden’s overall index score of 85 suggests that the
                        country is on average 85% of the way to the best possible outcome across the 17 SDGs.
                    </p>

                    <p>* Source: Bertelsmann Stiftung, Sustainable Development Solutions Network 2018</p>
                </SmallPrint> */}

                <p style={{ textAlign: "right", width: "100%" }}>
                    {download ? (
                        <a href={image?.url} download={image?.name} className="Button">
                            Save as image
                        </a>
                    ) : null}
                </p>
            </Flex>
        </>
    );
}

export default CompareSweden;
